import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronRight, FaExclamationTriangle } from 'react-icons/fa';

import {
  useLazyGetCompaniesAtAddressQuery,
  CompanyAtAddressItem,
} from '@client/api';

import {
  OnboardingBuilding,
  ImportCompanyCandidate,
} from '../../onboarding/onboarding.types';
import { OnboardingActions } from '../../onboarding/onboarding-actions';

interface StepImporterProps {
  buildings: OnboardingBuilding[];
  importCompanies: ImportCompanyCandidate[];
  selfOrgNr: string | null;
  onImportCompaniesChange: (candidates: ImportCompanyCandidate[]) => void;
  onBack: () => void;
  onNext: () => void;
}

interface BuildingLookupState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  error?: string;
  companies: CompanyAtAddressItem[];
  /** Lookup was skipped because the building is outside Norway (BRREG is Norway-only). */
  norwayOnly?: boolean;
}

export function StepImporter({
  buildings,
  importCompanies,
  selfOrgNr,
  onImportCompaniesChange,
  onBack,
  onNext,
}: StepImporterProps) {
  const { t } = useTranslation('portfolio');
  const selectedBuildings = useMemo(() => buildings.filter((b) => b.selected), [buildings]);

  const [lookups, setLookups] = useState<Record<string, BuildingLookupState>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [triggerFetch] = useLazyGetCompaniesAtAddressQuery();
  // Ensures we auto-open only the first building that returns companies — the user
  // opens the rest themselves.
  const hasAutoOpenedRef = useRef(false);

  // Auto-fetch companies for each selected building on mount.
  //
  // CARE: two subtle issues fixed here that used to cause the UI to flash "N / N valgt"
  // and then bounce to "0 / 0" when the user had multiple buildings selected:
  //
  //  1) **Stale-closure wipe across iterations.** Each iteration used to call a helper
  //     that read `importCompanies` from the component closure to compute
  //     `others = importCompanies.filter(...)`. Inside this for-loop the parent has
  //     not re-rendered yet between iterations, so iteration 2 read the *initial*
  //     value of `importCompanies` (often `[]`), wiping iteration 1's successful
  //     results when iteration 2 returned 0 companies. Fixed by using a local
  //     `accumulated` variable that we mutate ourselves as we go — iteration N sees
  //     iteration N-1's write immediately.
  //
  //  2) **Strict Mode double-invocation skip-trap.** The skip guard also short-
  //     circuited on `'loading'`. In React 18 Strict Mode dev, the effect runs twice:
  //     the first invocation sets `loading`, is cancelled before its fetch commits,
  //     and the second invocation would then see `'loading'` and skip → the fetch
  //     never effectively completed. Fixed by skipping only on terminal states
  //     (`ready` / `error`).
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // Local accumulator — seeded from the current importCompanies prop at effect
      // start. Updated synchronously after each building's fetch so the next
      // iteration sees our in-flight writes rather than stale props.
      let accumulated: ImportCompanyCandidate[] = importCompanies;

      for (const building of selectedBuildings) {
        if (!building.postalCode || !building.address) continue;
        const status = lookups[building.id]?.status;
        if (status === 'ready' || status === 'error') continue;

        // The companies-at-address lookup is backed by BRREG and only covers Norway;
        // foreign buildings get an explanatory empty state instead of a doomed request.
        if (building.countryThreeLetterCode && building.countryThreeLetterCode !== 'NOR') {
          setLookups((prev) => ({
            ...prev,
            [building.id]: { status: 'ready', companies: [], norwayOnly: true },
          }));
          continue;
        }

        setLookups((prev) => ({ ...prev, [building.id]: { status: 'loading', companies: [] } }));

        try {
          const res = await triggerFetch({
            postalCode: building.postalCode,
            address: building.address,
          }).unwrap();

          if (cancelled) return;

          const companies = res.companies ?? [];
          setLookups((prev) => ({
            ...prev,
            [building.id]: { status: 'ready', companies },
          }));

          // Pre-select all by default (matches "Import default" product decision).
          // Mutate the local accumulator and push it to the parent. Empty-result
          // buildings still participate but only remove *their own* prior rows
          // — they can no longer wipe other buildings' results.
          const fresh: ImportCompanyCandidate[] = companies.map((c) => ({
            ...c,
            sourceBuildingId: building.id,
            selected: c.orgNumber !== selfOrgNr,
          }));
          const others = accumulated.filter((c) => c.sourceBuildingId !== building.id);
          accumulated = mergeByOrgNumber([...others, ...fresh]);
          onImportCompaniesChange(accumulated);

          // Auto-open ONLY the first building that returns leietakere so the user
          // sees immediate results without an overwhelming all-expanded view.
          if (!hasAutoOpenedRef.current && companies.length > 0) {
            hasAutoOpenedRef.current = true;
            setExpanded((prev) => ({ ...prev, [building.id]: true }));
          }
        } catch (err: any) {
          if (cancelled) return;
          setLookups((prev) => ({
            ...prev,
            [building.id]: {
              status: 'error',
              companies: [],
              error: err?.data?.detail ?? err?.message ?? 'Kunne ikke hente leietakere',
            },
          }));
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuildings.length]);

  const toggleCompany = (orgNumber: string) => {
    onImportCompaniesChange(
      importCompanies.map((c) =>
        c.orgNumber === orgNumber ? { ...c, selected: !c.selected } : c,
      ),
    );
  };

  const toggleBuildingExpanded = (buildingId: string) => {
    // Default expanded state is `false` (only the first building with results is
    // auto-opened). Flip against the effective value to keep the first click predictable.
    setExpanded((prev) => ({ ...prev, [buildingId]: !(prev[buildingId] ?? false) }));
  };

  const toggleAllInBuilding = (buildingId: string, nextValue: boolean) => {
    onImportCompaniesChange(
      importCompanies.map((c) =>
        c.sourceBuildingId === buildingId && c.orgNumber !== selfOrgNr
          ? { ...c, selected: nextValue }
          : c,
      ),
    );
  };

  const totalSelected = importCompanies.filter((c) => c.selected).length;
  const totalFound = importCompanies.length;

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Importer leietakere</h2>
      <p className="text-white/60 mb-6">
        Vi har funnet selskaper registrert på adressene til byggene dine. Velg hvilke som skal legges inn som
        leietakere i systemet. Dere kan alltids fjerne eller legge til flere senere.
      </p>

      <div className="mb-6 p-4 rounded-lg border border-border bg-surface-base flex items-center justify-between">
        <div>
          <div className="text-white text-sm">
            <span className="font-bold text-xl">{totalSelected}</span>
            <span className="text-white/50"> / {totalFound}</span>
          </div>
          <div className="text-white/50 text-xs mt-1">leietakere valgt</div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() =>
              onImportCompaniesChange(
                importCompanies.map((c) =>
                  c.orgNumber === selfOrgNr ? c : { ...c, selected: true },
                ),
              )
            }
            className="text-xs px-3 py-1 rounded border border-border text-white/70 hover:text-white hover:border-white/30"
          >
            Velg alle
          </button>
          <button
            type="button"
            onClick={() => onImportCompaniesChange(importCompanies.map((c) => ({ ...c, selected: false })))}
            className="text-xs px-3 py-1 rounded border border-border text-white/70 hover:text-white hover:border-white/30"
          >
            Fjern alle
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {selectedBuildings.map((building) => {
          const state = lookups[building.id];
          const buildingCandidates = importCompanies.filter((c) => c.sourceBuildingId === building.id);
          const buildingSelected = buildingCandidates.filter((c) => c.selected).length;
          const isExpanded = expanded[building.id] ?? false;

          return (
            <div key={building.id} className="rounded-lg border border-border bg-surface-base overflow-hidden">
              <button
                type="button"
                onClick={() => toggleBuildingExpanded(building.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-elevated transition-colors"
              >
                {isExpanded ? (
                  <FaChevronDown className="text-white/50 text-xs" />
                ) : (
                  <FaChevronRight className="text-white/50 text-xs" />
                )}
                <div className="flex-1">
                  <div className="text-white font-medium">{building.address}</div>
                  <div className="text-white/50 text-xs">
                    {building.postalCode} {building.city}
                  </div>
                </div>
                <div className="text-sm text-white/70">
                  {state?.status === 'loading' && 'Henter…'}
                  {state?.status === 'error' && (
                    <span className="text-red-400 flex items-center gap-1">
                      <FaExclamationTriangle /> Feil
                    </span>
                  )}
                  {state?.status === 'ready' && (
                    <span>
                      <span className="text-white font-bold">{buildingSelected}</span>
                      <span className="text-white/40"> / {buildingCandidates.length}</span>
                      <span className="text-white/40"> valgt</span>
                    </span>
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-border">
                  {state?.status === 'loading' && (
                    <div className="px-5 py-4 text-sm text-white/50">Henter selskaper…</div>
                  )}

                  {state?.status === 'error' && (
                    <div className="px-5 py-4 text-sm text-red-400">{state.error}</div>
                  )}

                  {state?.status === 'ready' && buildingCandidates.length === 0 && (
                    <div className="px-5 py-4 text-sm text-white/50">
                      {state.norwayOnly
                        ? t('onboarding.importer.norwayOnly')
                        : 'Ingen selskaper registrert på denne adressen.'}
                    </div>
                  )}

                  {state?.status === 'ready' && buildingCandidates.length > 0 && (
                    <>
                      <div className="px-5 py-2 border-b border-border flex items-center justify-end gap-2 text-xs">
                        <button
                          type="button"
                          onClick={() => toggleAllInBuilding(building.id, true)}
                          className="text-white/60 hover:text-white"
                        >
                          Velg alle i dette bygget
                        </button>
                        <span className="text-white/20">|</span>
                        <button
                          type="button"
                          onClick={() => toggleAllInBuilding(building.id, false)}
                          className="text-white/60 hover:text-white"
                        >
                          Fjern alle
                        </button>
                      </div>

                      <div className="grid grid-cols-[40px_1fr_180px_80px_140px] gap-3 px-5 py-2 text-[10px] text-white/40 uppercase tracking-wider border-b border-border">
                        <span />
                        <span>Selskap</span>
                        <span>Bransje</span>
                        <span className="text-right">Ansatte</span>
                        <span>Status</span>
                      </div>

                      {buildingCandidates.map((c) => {
                        const isSelf = c.orgNumber === selfOrgNr;

                        return (
                          <label
                            key={c.orgNumber}
                            className={`grid grid-cols-[40px_1fr_180px_80px_140px] gap-3 px-5 py-2 border-b border-border last:border-b-0 cursor-pointer transition-colors ${
                              c.selected ? 'bg-brand-primary/5' : 'hover:bg-surface-elevated'
                            } ${isSelf ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={c.selected}
                                onChange={() => !isSelf && toggleCompany(c.orgNumber)}
                                disabled={isSelf}
                                className="w-4 h-4 accent-brand-primary"
                              />
                            </div>
                            <div className="min-w-0">
                              <div className="text-white text-sm font-medium truncate">{c.name}</div>
                              <div className="text-white/40 text-xs">Org.nr. {c.orgNumber}</div>
                            </div>
                            <div className="text-white/60 text-xs flex items-center truncate">
                              {c.industryCode ?? '—'}
                            </div>
                            <div className="text-white/70 text-sm flex items-center justify-end tabular-nums">
                              {c.numberOfEmployees ?? '—'}
                            </div>
                            <div className="flex items-center gap-1 flex-wrap">
                              {isSelf && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/60">
                                  Deg selv
                                </span>
                              )}
                              {c.existsInDb && !isSelf && (
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300"
                                  title="Selskapet finnes allerede i systemet og vil bli koblet til din konto"
                                >
                                  I systemet
                                </span>
                              )}
                              {c.isBankrupt && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                                  Konkurs
                                </span>
                              )}
                              {c.isUnderLiquidation && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">
                                  Avvikling
                                </span>
                              )}
                              {c.isUnderForcedDissolution && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                                  Tvangsavvikling
                                </span>
                              )}
                              {c.isBranch && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/50">
                                  Avdeling
                                </span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedBuildings.length === 0 && (
        <div className="p-6 rounded-lg border border-border bg-surface-base text-center text-white/50">
          Ingen bygg valgt. Gå tilbake til forrige steg for å velge bygg.
        </div>
      )}

      <OnboardingActions onBack={onBack} onNext={onNext} />
    </div>
  );
}

/**
 * When a company appears at multiple selected buildings, keep the first occurrence so the
 * same org.nr isn't listed twice. This matches the backend's internal dedup on org number.
 */
function mergeByOrgNumber(items: ImportCompanyCandidate[]): ImportCompanyCandidate[] {
  const seen = new Set<string>();
  const out: ImportCompanyCandidate[] = [];
  for (const item of items) {
    if (seen.has(item.orgNumber)) continue;
    seen.add(item.orgNumber);
    out.push(item);
  }
  return out;
}
